import React from 'react';
import { Link } from 'react-router-dom';
import { useUserRole } from '../../contexts/UserRoleContext';
import { NAV_VISIBLE_SECTIONS } from 'config/sections';
import './Navigation.css';

interface NavigationProps {
  onSectionChange: (section: string) => void;
  /** Slug for current location (e.g. "oakland"); null when no location selected */
  locationSlug: string | null;
  /** Current pathname for building links and aria-current */
  currentPath: string;
}

type NavColor = 'blue' | 'teal' | 'light-blue' | 'gold' | 'purple' | 'orange' | 'pink' | 'gray' | 'slate';

type NavItem = {
  section: string;
  label: string;
  shortLabel: string;
  color: NavColor;
};

function buildSectionPath(slug: string | null, section: string): string {
  if (section === 'law-impact') return '/law-impact';
  if (!slug) return section === 'overview' ? '/overview' : `/${section}`;
  return section === 'overview' ? `/${slug}` : `/${slug}/${section}`;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange: _onSectionChange, locationSlug, currentPath }) => {
  const { hasAnyRole } = useUserRole();

  const baseNavItems: NavItem[] = [
    { section: 'overview', label: 'Overview →', shortLabel: 'Overview', color: 'blue' },
    { section: 'transportation', label: 'Transportation →', shortLabel: 'Transport', color: 'light-blue' },
    { section: 'buildings', label: 'Buildings →', shortLabel: 'Buildings', color: 'gold' },
    { section: 'other', label: 'Waste +', shortLabel: 'Waste+', color: 'orange' },
    { section: 'people', label: 'People →', shortLabel: 'People', color: 'pink' },
    { section: 'ghg', label: 'GHG inventory →', shortLabel: 'GHG', color: 'teal' },
  ];

  const publicNavItems: NavItem[] = [
    ...baseNavItems,
    { section: 'policy-tool', label: 'GHG Scenarios →', shortLabel: 'Scenarios', color: 'purple' },
  ];

  const showCalculator = hasAnyRole(['super_admin', 'developer']);
  const showAnalysis = hasAnyRole(['developer', 'super_admin', 'local_govt_admin', 'academic']);
  const showLawImpact = hasAnyRole(['developer', 'super_admin', 'local_govt_admin', 'academic']);
  let navItems: NavItem[] = showCalculator
    ? [...publicNavItems, { section: 'coolclimate-calculator', label: 'Calculator →', shortLabel: 'Calc', color: 'gray' }]
    : publicNavItems;
  if (showAnalysis) {
    navItems = [...navItems, { section: 'analysis', label: 'Analysis →', shortLabel: 'Analysis', color: 'slate' }];
  }
  if (showLawImpact) {
    navItems = [...navItems, { section: 'law-impact', label: 'Policies →', shortLabel: 'Policies', color: 'slate' }];
  }

  const handleNavClick = (section: string, e: React.MouseEvent | React.KeyboardEvent) => {
    const knownSections = NAV_VISIBLE_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    const isOverviewPath =
      parts.length === 0 ||
      (parts.length === 1 && !knownSections.has(parts[0])) ||
      (parts.length === 2 && parts[1] === 'overview');
    const isOnOverviewPage = isOverviewPath;
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;

    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      e.preventDefault();
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const knownSections = NAV_VISIBLE_SECTIONS;
    const parts = currentPath.split('/').filter(Boolean);
    if (parts.length === 0) return 'overview';
    if (parts.length === 1) return knownSections.has(parts[0]) ? parts[0] : 'overview';
    return parts[1] || 'overview';
  };

  const currentSection = getCurrentSection();

  return (
    <nav className="nav-bar" aria-label="Main navigation">
      <div className="nav-items" role="list">
        {navItems.map((item) => {
          const path = buildSectionPath(locationSlug, item.section);
          const isActive = currentSection === item.section;
          const aria = item.label.replace(/\s*→\s*$/, '').trim();
          return (
            <Link
              key={item.section}
              to={path}
              className={`nav-item ${item.color} ${isActive ? 'active' : ''}`}
              onClick={(e) => handleNavClick(item.section, e)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={aria}
              role="listitem"
            >
              <span className="nav-item-label nav-item-label--full">{item.label}</span>
              <span className="nav-item-label nav-item-label--compact">{item.shortLabel}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
